import { ContextColumnsBase } from "@mwt/web-js-telemetry";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { updateLoggingContext } from "../telemetry-logger/logger";
import { TELEMETRY_SESSION_KEY } from "../utils/constants";

import { SessionContext } from "./SessionContext";
import { SessionContextProviderProps } from "./SessionContext.types";

export const SessionContextProvider: React.FC<
  React.PropsWithChildren<SessionContextProviderProps>
> = ({ children }) => {
  /**
   * Session ID is a constant that we read from the sessionStorage.
   * This allows us to correlate a user's session in our logs.
   * This is used to form the correlation-id string, and add it as a property
   * to all telemetry logs inside of logger.
   * We also persist this within a session per origin per tab of the browser.
   */
  const [sesId, setSesId] = useState<string | null>(
    sessionStorage.getItem(TELEMETRY_SESSION_KEY)
  );

  const setSessionId = useCallback((sessionId: any) => {
    setSesId(sessionId);
    sessionStorage.setItem(TELEMETRY_SESSION_KEY, sessionId);
  }, []);

  // `useMemo` is used for preventing the  value reference to update on subsequent re-renders.
  const value = useMemo(
    () => ({ sessionId: sesId, setSessionId }),
    [sesId, setSessionId]
  );

  /**
   * Sets a new `sesId` if it doesn't exist on mount.
   */
  useEffect(() => {
    if (!sesId) {
      const initSessionId = uuid();
      setSessionId(initSessionId);
    }
  }, []);

  // This effect updates the sessionId logging context.
  useEffect(() => {
    if (sesId) {
      updateLoggingContext({ [ContextColumnsBase.SessionId]: sesId });
    }
  }, [sesId]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSessionContext = () => useContext(SessionContext);
